import React, { useState, useEffect } from 'react';
import DatePicker from '../../../components/datePicker';
import { LomavisH2 } from '../../../lomavisSharedUi/atoms/typography/LomavisH2';
import IsLoadingIndicator from '../../../components/isLoadingIndicator';
import { LomavisGrowthCardSectionTemplate } from '../../../lomavisSharedUi/templates/cards/LomavisGrowthCardSectionTemplate';
import { useTranslate } from '@tolgee/react';
import { getAllDates } from '../../../utils/dateUtils';
import { getGrowthMetricsEntries } from '../../../utils/analytics/growthMetricsUtils';
import NewPlotTabs from '../../../components/plotTabs/newPlotTabs';
import { 
  instagramFollowersGrowthConfig,
  instagramPlatformsGrowthConfig,
  instagramDailyGrowthConfig,
  instagramContentGrowthConfig
} from './growthConfig';
import { 
  getAnalyticsForLocation,
  transferData
} from '../../../redux/slices/analytics/plotsData';
import { ANALYICS_FOR_SOCIAL_MEDIA } from '../../../config/firebase/collections';
import { INSTAGRAM } from '../../../config/constants';
import { useSelector } from 'react-redux';

interface InstagramOverviewTabProps {
  dates: any;
  setDates: (dates: any) => void;
  plotsData: any;
  loading: boolean;
  error: boolean;
  newConfigsMap: any;
}

const InstagramOverviewTab: React.FC<InstagramOverviewTabProps> = ({
  dates,
  setDates,
  plotsData,
  loading,
  error,
  newConfigsMap
}) => {
  const { t } = useTranslate();
  const [oldPlotsData, setOldPlotsData] = useState(null);
  const [loadingOld, setLoadingOld] = useState(true);
  
  const { uuid, platforms_information } = useSelector(
    (state: any) => state.locationDetails
  );

  // Fetch old data for comparison
  useEffect(() => {
    if (!uuid || !dates || loading) return;

    const dateRange = getAllDates(dates.startDate, dates.endDate).length;
    const oldStartDate = new Date(dates.startDate);
    oldStartDate.setDate(oldStartDate.getDate() - dateRange);

    const oldEndDate = new Date(dates.startDate);
    oldEndDate.setDate(oldEndDate.getDate() - 1);

    const oldDates = {
      startDate: oldStartDate,
      endDate: oldEndDate
    };

    if (platforms_information && platforms_information[INSTAGRAM]?.[0]?.uuid) {
      setLoadingOld(true);
      getAnalyticsForLocation(
        ANALYICS_FOR_SOCIAL_MEDIA,
        'social_media_profile',
        platforms_information[INSTAGRAM]?.[0]?.uuid,
        oldDates,
        INSTAGRAM
      )
        .then((oldRes) => {
          if (oldRes) {
            const oldData = transferData(oldRes, newConfigsMap);
            setOldPlotsData(oldData);
          }
          setLoadingOld(false);
        })
        .catch((err) => {
          console.log(err, 'fetch old data error');
          setLoadingOld(false);
        });
    }
  }, [uuid, dates, loading, platforms_information, newConfigsMap]);

  return (
    <div>
      <div className="d-flex flex-column flex-lg-row justify-content-lg-end">
        <DatePicker setDates={setDates} />
      </div>
      <div className="m-5" />
      <LomavisH2 title={t('platform_analytics.title.KPISection')} />
      
      {plotsData?.plots &&
        Object.keys(plotsData?.plots).length > 0 &&
        !loading && 
        !loadingOld && (
          <>
            <div className={'mb-5'} />

            <div className="mb-10">
              {plotsData?.plots && (
                <LomavisGrowthCardSectionTemplate
                  title={t('platform_analytics.followers')}
                  entries={getGrowthMetricsEntries(
                    plotsData?.plots || {},
                    oldPlotsData?.plots || {},
                    instagramFollowersGrowthConfig,
                    t,
                    !loadingOld
                  )}
                />
              )}
            </div>

            <div className="mb-10">
              {plotsData?.plots && (
                <LomavisGrowthCardSectionTemplate
                  title={t('platform_analytics.growth_overview')}
                  entries={getGrowthMetricsEntries(
                    plotsData?.plots || {},
                    oldPlotsData?.plots || {},
                    instagramPlatformsGrowthConfig,
                    t,
                    !loadingOld
                  )}
                />
              )}
            </div>

            <div className={'mb-10'}>
              {plotsData?.plots && (
                <LomavisGrowthCardSectionTemplate
                  title={t('platform_analytics.growth_daily_overview')}
                  entries={getGrowthMetricsEntries(
                    plotsData?.plots || {},
                    oldPlotsData?.plots || {},
                    instagramDailyGrowthConfig,
                    t,
                    !loadingOld
                  )}
                />
              )}
            </div>

            <div className={'mb-10'}>
              {plotsData?.plots && (
                <LomavisGrowthCardSectionTemplate
                  title={t('platform_analytics.content_growth_overview')}
                  entries={getGrowthMetricsEntries(
                    plotsData?.plots || {},
                    oldPlotsData?.plots || {},
                    instagramContentGrowthConfig,
                    t,
                    !loadingOld
                  )}
                />
              )}
            </div>
            <div className={'mb-5'} />
          </>
        )}
      
      {!plotsData?.plots && !loading && !error && (
        <div
          className="d-flex justify-content-center align-items-center m-4"
          style={{ height: '200px' }}
        >
          <p className="text-muted">
            {t('platform_analytics.no_data_available')}
          </p>
        </div>
      )}

      {error && (
        <div
          className="d-flex justify-content-center align-items-center m-4"
          style={{ height: '200px' }}
        >
          <LomavisH2 title={t('platform_analytics.error_loading_data')} />
        </div>
      )}

      {(loading || loadingOld) && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '200px' }}
        >
          <IsLoadingIndicator />
        </div>
      )}

      <div className="m-5" />
      <LomavisH2 title={t('platform_analytics.title.PlotSection')} />
      <div className={'mb-5'} />

      <NewPlotTabs
        hasError={error}
        labelsLength={getAllDates(dates.startDate, dates.endDate).length}
        data={plotsData}
        config={newConfigsMap}
        display={true}
        isLoading={loading}
      />
    </div>
  );
};

export default InstagramOverviewTab;